"use client";

import type { Slot } from "../../types/calendar.ts";
import type { CalendarConfig } from "@/features/calendar/types/calendar-config.ts";
import type React from "react";

import { motion } from "motion/react";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify-icon/react";

import { useHostingOptions } from "../../types/hostingOptions.tsx";

import { useCalendarStore } from "@/features/calendar/store/useCalendarStore.tsx";
import { parseTime } from "@/features/calendar/utils/date.ts";
import { useCalendarModalStore } from "@/features/calendar/store/useCalendarModalStore.tsx";
import useUserState from "@/hooks/useUserState.tsx";
import useUserStore from "@/features/auth/store/useUserStore.ts";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar.tsx";
import { getEventColor } from "@/features/calendar/utils/slots.ts";
import { LessonHost } from "@/features/calendar/types/create-slot.ts";
import AppLogoIcon from "@/components/LogoIcon.tsx";

// Extend the Slot type to include visual rendering properties
type ExtendedSlot = Slot & {
  visualStartTime?: string;
  visualEndTime?: string;
};

interface EventComponentProps {
  event: ExtendedSlot;
  dayIndex: number;
  weekDaysLength: number;
  finalConfig: CalendarConfig;
  canMakeReservations?: boolean;
}

export const StudentSlotComponent: React.FC<EventComponentProps> = ({
  event,
  finalConfig,
  canMakeReservations,
}) => {
  const { t } = useTranslation("common");
  const { hourHeight, minHour, maxHour } = finalConfig;
  const isAbleToReserve =
    useUserState() === "studentInWorkspace" &&
    event.status === "available" &&
    canMakeReservations !== false;
  const { shownDays } = useCalendarStore();
  const { user } = useUserStore();
  const hostingOptions = useHostingOptions();

  // Determine the effective status - if reservations are disabled and slot is available, show as unavailable
  const effectiveStatus =
    event.status === "available" && canMakeReservations === false ? "unavailable" : event.status;

  // Use visual times for rendering if available, otherwise use actual times
  const [startHour, startMinute] = parseTime(event.visualStartTime || event.startTime);
  const [endHour, endMinute] = parseTime(event.visualEndTime || event.endTime);

  const { setIsOpen, setSlot, setAction } = useCalendarModalStore();

  const start = startHour + startMinute / 60;
  const end = endHour + endMinute / 60;
  const height = Math.min((end - start) * hourHeight, (maxHour - minHour) * hourHeight) - 4;
  const top = (start - minHour) * hourHeight + (height <= hourHeight / 2 ? 0 : 3);

  // Calculate slot duration in minutes
  const slotDurationMinutes = (end - start) * 60;

  // Check if this is a short slot (less than 30 minutes)
  const isShortSlot = slotDurationMinutes < 30;
  // Check if this is a medium slot (less than 45 minutes) - only show title and content for 45+ minutes
  const isMediumSlot = slotDurationMinutes < 45;

  // Get participants excluding the current user
  const otherParticipants =
    event.lesson?.participants?.filter((participant) => participant.id !== user?.id) || [];

  // Get hosting method display info
  const getHostDisplayInfo = (lessonHost?: LessonHost) => {
    if (!lessonHost) return null;

    const option = hostingOptions.find((opt) => opt.id === lessonHost);

    if (!option) return null;

    let icon;
    let shortName;

    if (lessonHost === LessonHost.Ximly) {
      icon = <AppLogoIcon className="size-3" />;
      shortName = option.title.split(" ")[0];
    } else if (lessonHost === LessonHost.GoogleMeets) {
      icon = <Icon height="0.75rem" icon="logos:google-meet" width="0.75rem" />;
      shortName = option.title.split(" ")[0];
    } else if (lessonHost === LessonHost.Zoom) {
      icon = <Icon height="0.75rem" icon="logos:zoom" width="0.75rem" />;
      shortName = option.title.split(" ")[0];
    } else {
      icon = <Icon height="0.75rem" icon="ph:globe" width="0.75rem" />;
      shortName = option.title.split(" ")[0];
    }

    return {
      icon,
      shortName,
      fullTitle: option.title,
    };
  };

  const hostInfo = getHostDisplayInfo(event.lessonHost as LessonHost);

  // Skip rendering if outside visible hours
  if (end <= minHour || start >= maxHour) {
    return null;
  }

  return (
    <motion.div
      animate={{ opacity: 1 }}
      className={`group absolute right-0.5 left-0.5 z-20 !w-[95%] cursor-pointer rounded-sm border-l-4 ${getEventColor(effectiveStatus).border} bg-foreground/75 shadow-sm transition-all hover:z-25 hover:shadow-md ${
        shownDays > 3 ? "hover:scale-[1.02]" : "hover:scale-[1.01]"
      } ${shownDays > 3 && "scale-[1.01]"}`}
      exit={{ opacity: 0 }}
      initial={{ opacity: 0 }}
      style={{
        top: `${Math.max(top, 0)}px`,
        height: isMediumSlot
          ? `${(end - start) * hourHeight}px`
          : `${Math.max(height, hourHeight / 1.5) - 3}px`,
      }}
      onClick={(e) => {
        if (isAbleToReserve) {
          e.stopPropagation();
          setAction("reservation");
          // If this is a split slot, we need to pass the original slot ID
          const originalId = event.id.includes("-part") ? event.id.split("-part")[0] : event.id;
          // Create a copy of the event with the original ID for reservation
          const originalEvent = { ...event, id: originalId };

          setSlot(originalEvent, user?.role);
          setIsOpen(true);
        }
        if (event.status === "booked") {
          setAction("details");
          const originalId = event.id.includes("-part") ? event.id.split("-part")[0] : event.id;
          // Create a copy of the event with the original ID for reservation
          const originalEvent = { ...event, id: originalId };

          setSlot(originalEvent, user?.role);
          setIsOpen(true);
        }
      }}
    >
      {/* Status Badge - only show for slots 45+ minutes */}
      {!isMediumSlot && (
        <div
          className={`absolute top-2 right-1 z-30 rounded-full border px-1 py-0.5 text-[9px] font-medium ${getEventColor(effectiveStatus).badge}`}
        >
          {effectiveStatus === "available" && t("calendar.slot.available")}
          {effectiveStatus === "expired" && t("calendar.slot.expired")}
          {effectiveStatus === "booked" && t("calendar.slot.booked")}
          {effectiveStatus === "booked_by_others" && t("calendar.slot.bookedByOther")}
          {effectiveStatus === "unavailable" && t("calendar.slot.unavailable")}
        </div>
      )}

      {/* Content for short slots - only time and badge */}
      {isShortSlot ? (
        <div className="flex h-full items-center justify-between px-2">
          <div
            className={`text-[8px] ${
              effectiveStatus === "expired"
                ? "text-muted-foreground line-through"
                : "text-muted-foreground"
            }`}
          >
            {`${event.startTime} - ${event.endTime}`}
          </div>
          {/* Status Badge for short slots - no y padding */}
          <div
            className={`rounded-full border px-1 py-0.5 text-[8px] font-medium ${getEventColor(effectiveStatus).badge}`}
          >
            {effectiveStatus === "available" && t("calendar.slot.available")}
            {effectiveStatus === "expired" && t("calendar.slot.expired")}
            {effectiveStatus === "booked" && t("calendar.slot.booked")}
            {effectiveStatus === "booked_by_others" && t("calendar.slot.bookedByOther")}
            {effectiveStatus === "unavailable" && t("calendar.slot.unavailable")}
          </div>
        </div>
      ) : isMediumSlot ? (
        <div className="flex h-full items-center justify-between px-2">
          <div
            className={`text-xs ${
              effectiveStatus === "expired"
                ? "text-muted-foreground line-through"
                : "text-muted-foreground"
            }`}
          >
            {`${event.startTime} - ${event.endTime}`}
          </div>
          {/* Status Badge for medium slots */}
          <div
            className={`rounded-full border px-1 py-0.5 text-[9px] font-medium ${getEventColor(effectiveStatus).badge}`}
          >
            {effectiveStatus === "available" && t("calendar.slot.available")}
            {effectiveStatus === "expired" && t("calendar.slot.expired")}
            {effectiveStatus === "booked" && t("calendar.slot.booked")}
            {effectiveStatus === "booked_by_others" && t("calendar.slot.bookedByOther")}
            {effectiveStatus === "unavailable" && t("calendar.slot.unavailable")}
          </div>
        </div>
      ) : (
        <div className="flex h-full flex-col p-2">
          <div className="flex flex-col">
            <h3
              className={`text-sm font-medium ${
                effectiveStatus === "expired"
                  ? "text-muted-foreground line-through"
                  : "text-accent-foreground"
              }`}
            >
              {t("calendar.slot.whichLessonType", {
                type: t(
                  //@ts-ignore
                  `calendar.slot.${event.lessonType}`,
                ),
              })}
            </h3>

            {/* Time with conditional host method layout */}
            <div className="flex flex-col">
              <div className="flex items-center gap-1">
                <div
                  className={`text-xs ${
                    effectiveStatus === "expired"
                      ? "text-muted-foreground line-through"
                      : "text-muted-foreground"
                  }`}
                >
                  {`${event.startTime} - ${event.endTime}`}
                  {/* Show host inline for short slots */}
                  {hostInfo && slotDurationMinutes < 60 && (
                    <>
                      <span className="mx-1">•</span>
                      <span className="inline-flex items-center gap-1" title={hostInfo.fullTitle}>
                        {hostInfo.icon}
                        <span>{hostInfo.shortName}</span>
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Show host below for longer slots */}
              {hostInfo && slotDurationMinutes >= 60 && (
                <div className="mt-1 flex items-center gap-1">
                  <span
                    className="text-muted-foreground inline-flex items-center gap-1 text-xs"
                    title={hostInfo.fullTitle}
                  >
                    {hostInfo.icon}
                    <span>{hostInfo.shortName}</span>
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Participant Avatars - only show if slot is booked and has enough height */}
      {effectiveStatus === "booked" &&
        slotDurationMinutes >= 45 &&
        otherParticipants.length > 0 && (
          <div className="absolute right-2 bottom-1 z-30 flex -space-x-2">
            {otherParticipants.slice(0, 4).map((participant, index) => (
              <Avatar
                key={participant.id}
                className="border-background size-5 border"
                style={{ zIndex: 40 - index }}
                title={`${participant.name}`}
              >
                <AvatarImage
                  alt={participant.name}
                  src={participant.avatar || "/placeholder.svg"}
                />
                <AvatarFallback className="text-xs">
                  {participant.name && participant.name.substring(0, 2)}
                </AvatarFallback>
              </Avatar>
            ))}
            {otherParticipants.length > 4 && (
              <div
                className="border-background bg-muted text-muted-foreground flex size-5 items-center justify-center rounded-full border-2 text-xs font-medium"
                style={{ zIndex: 35 }}
              >
                +{otherParticipants.length - 4}
              </div>
            )}
          </div>
        )}
    </motion.div>
  );
};
